import React from 'react';
import { useTranslations } from 'react-ui';
import { FormFooter } from '../common/FormFooter';

interface AiMultiEventResultProps {
  isSavingDrafts: boolean;
  savedCount: number;
  totalCount: number;
  eventTitles: string[];
  onViewDrafts: () => void;
  onCreateManually: () => void;
}

/**
 * Success screen shown after multiple events are extracted from an image
 * and saved as drafts. Replaces the AI entry panel.
 */
export const AiMultiEventResult: React.FC<AiMultiEventResultProps> = ({
  isSavingDrafts,
  savedCount,
  totalCount,
  eventTitles,
  onViewDrafts,
  onCreateManually,
}) => {
  const { translated } = useTranslations();

  if (isSavingDrafts) {
    const progress = totalCount > 0 ? Math.round((savedCount / totalCount) * 100) : 0;

    return (
      <div className="flex flex-col items-center justify-center gap-6 py-8 px-4 text-center h-full">
        <svg className="animate-spin h-10 w-10 text-primary-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>

        <div>
          <p className="text-base font-semibold text-gray-900">
            {translated('sc_ai_multi_result_saving')
              .replace('{saved}', String(savedCount))
              .replace('{total}', String(totalCount))}
          </p>
          <div className="mt-3 w-48 h-2 rounded-full bg-gray-200 overflow-hidden mx-auto">
            <div
              className="h-full bg-primary-500 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
              role="progressbar"
              aria-valuenow={savedCount}
              aria-valuemin={0}
              aria-valuemax={totalCount}
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">{savedCount} / {totalCount}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 h-full py-4">
      <div className="flex flex-col items-center text-center gap-2">
        <span className="text-4xl" aria-hidden="true">✅</span>
        <h3 className="text-lg font-bold text-gray-900">
          {translated('sc_ai_multi_result_title').replace('{count}', String(savedCount))}
        </h3>
        <p className="text-sm text-gray-600 max-w-sm">
          {translated('sc_ai_multi_result_body')}
        </p>
      </div>

      {eventTitles.length > 0 && (
        <ul className="flex flex-col gap-1.5 flex-1 min-h-0 overflow-y-auto">
          {eventTitles.map((title, i) => (
            <li
              key={i}
              className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-md px-3 py-2"
            >
              <span className="shrink-0 text-green-500" aria-hidden="true">✓</span>
              <span className="truncate">{title || translated('sc_untitled_draft')}</span>
            </li>
          ))}
        </ul>
      )}

      <FormFooter
        onCancel={onCreateManually}
        onContinue={onViewDrafts}
        cancelLabel={translated('sc_entry_form')}
        continueLabel={translated('sc_ai_multi_result_cta')}
      />
    </div>
  );
};
